import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { FileText, Shield, ShieldCheck } from 'lucide-react';
import { PrivacyPolicy } from './auth/PrivacyPolicy';
import { TermsOfUse } from './auth/TermsOfUse';
import logoIcon from '@/assets/loop-icon.png';

interface AboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AboutModal({ open, onOpenChange }: AboutModalProps) {
  const version = '1.5.4';
  const releaseDate = 'December 2025';
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <img src={logoIcon} alt="Patent Loop" className="h-10 w-auto" />
              <DialogTitle className="text-2xl font-bold">Patent Loop</DialogTitle>
              <span className="px-2.5 py-1 text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md">
                BETA
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Version {version} • {releaseDate}
            </div>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* About Section */}
            <div>
              <h3 className="text-lg font-semibold mb-1.5">About Patent Loop</h3>
              <p className="text-sm text-gray-700 mb-2">
                Patent Loop is a comprehensive patent analysis and prosecution management platform designed to streamline
                patent examination workflows, office action analysis, and patent research.
              </p>
              <p className="text-xs text-gray-600 italic">
                Powered by{' '}
                <a
                  href="https://openai.com/enterprise-privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  OpenAI's Enterprise GPT-4
                </a>
                {' '}and{' '}
                <a
                  href="https://www.anthropic.com/legal/archive/7e37c9e3-af36-4555-93f0-2c677d027003"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Claude AI
                </a>
                , tuned for patents by U.S. patent professionals. Built on proprietary retrieval augmentation techniques developed by ForGen AI.
              </p>
              <p className="text-xs text-gray-500 text-center mt-1 italic">Patent Pending</p>
            </div>

            <Separator />

            {/* Privacy & Confidentiality */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-emerald-900 mb-1">Privacy & Confidentiality</h3>
                  <p className="text-sm text-emerald-800 mb-1">
                    Your data is <strong>never used for AI model training</strong>. All documents, messages, and information are kept strictly confidential.
                  </p>
                  <p className="text-sm text-emerald-800">
                    Feedback and usage data are only accessed by Patent Loop personnel for the sole purpose of improving the application.
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Legal Documents */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Legal Information</h3>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="justify-start gap-2 h-auto py-2"
                  onClick={() => setShowPrivacyPolicy(true)}
                >
                  <Shield className="w-4 h-4 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold text-sm">Privacy Policy</div>
                    <div className="text-xs text-gray-600">Learn how we protect your data</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="justify-start gap-2 h-auto py-2"
                  onClick={() => setShowTermsOfUse(true)}
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold text-sm">Terms of Use</div>
                    <div className="text-xs text-gray-600">Review our terms and conditions</div>
                  </div>
                </Button>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold mb-1.5">Contact Us</h3>
              <p className="text-sm text-gray-700">
                For questions or support, please contact us at{' '}
                <a href="mailto:feedback@patentloop.com" className="text-blue-600 hover:underline">
                  feedback@patentloop.com
                </a>
              </p>
            </div>

            <Separator />

            {/* Powered By */}
            <div className="text-center space-y-1 -mb-2">
              <p className="text-xs text-gray-500">
                Powered by{' '}
                <a
                  href="https://forgen.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium"
                >
                  ForGen AI
                </a>
              </p>
              <p className="text-xs text-gray-400">
                © 2026 Patent Loop. All rights reserved.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <PrivacyPolicy
        open={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />

      {/* Terms of Use Modal */}
      <TermsOfUse
        open={showTermsOfUse}
        onClose={() => setShowTermsOfUse(false)}
      />
    </>
  );
}
